import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MdClose, MdPushPin, MdSearch } from 'react-icons/md'

import { HighlightedTitle } from './item'
import { FlyoutCard } from './styles'
import { IMenuLeaf } from './utils'

export const RAIL_WIDTH = 60
const FLYOUT_GAP = 8
const FLYOUT_WIDTH = 238
// Acima disso o cartão passa a rolar por dentro, com o cabeçalho fixo.
const FLYOUT_MAX_HEIGHT_RATIO = 0.7

interface IProps {
  /** Rótulo do cabeçalho: "FIXADOS", o nome do grupo ou "RECENTES". */
  title: string
  /** Distância do topo da janela até o ícone que abriu o cartão. */
  anchorTop: number
  items: IMenuLeaf[]
  activeRoute: string | null
  onNavigate: (route: string) => void
  onClose: () => void
  /** Marca o cabeçalho de FIXADOS com o alfinete. */
  withPinIcon?: boolean
  /** Busca: o cartão ganha um campo no topo e as linhas destacam o trecho casado. */
  searchable?: boolean
  searchTerm?: string
  onSearchChange?: (value: string) => void
  /** Fixados: as linhas podem ser arrastadas para mudar de ordem. */
  reorderable?: boolean
  onReorder?: (from: number, to: number) => void
}

/**
 * Cartão flutuante do menu recolhido. É o mesmo componente para as quatro origens do rail —
 * fixados, grupo, recentes e busca —, então elas não podem divergir de aparência.
 *
 * Fica em `position: fixed` alinhado ao ícone que o abriu; quando não cabe até o rodapé da
 * janela, sobe o quanto for preciso em vez de sair da tela.
 */
export const MenuFlyout: React.FC<IProps> = ({
  title,
  anchorTop,
  items,
  activeRoute,
  onNavigate,
  onClose,
  withPinIcon = false,
  searchable = false,
  searchTerm = '',
  onSearchChange,
  reorderable = false,
  onReorder
}) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const [dropIndex, setDropIndex] = useState<number | null>(null)

  useEffect(() => {
    if (searchable) {
      searchRef.current?.focus()
      return
    }

    // Abertura por teclado leva o foco para a primeira linha; por mouse, o foco não se mexe.
    const first = cardRef.current?.querySelector<HTMLAnchorElement>('a')
    first?.focus({ preventScroll: true })
  }, [searchable])

  function focusRow(direction: 1 | -1, from?: HTMLElement) {
    const rows = Array.from(cardRef.current?.querySelectorAll('a') || [])

    if (!rows.length) {
      return
    }

    const currentIndex = from ? rows.indexOf(from as HTMLAnchorElement) : -1

    const nextIndex =
      currentIndex === -1
        ? direction === 1
          ? 0
          : rows.length - 1
        : (currentIndex + direction + rows.length) % rows.length

    rows[nextIndex]?.focus()
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }

    event.preventDefault()

    const target = event.target as HTMLElement
    const isRow = target.tagName === 'A'

    // Arrastar não alcança quem navega por teclado: Alt+setas faz o mesmo movimento.
    if (reorderable && event.altKey && isRow) {
      const from = Number(target.dataset.orderIndex)
      const to = from + (event.key === 'ArrowDown' ? 1 : -1)

      if (!Number.isNaN(from) && to >= 0 && to < items.length) {
        onReorder?.(from, to)
      }

      return
    }

    focusRow(event.key === 'ArrowDown' ? 1 : -1, isRow ? target : undefined)
  }

  const maxHeight = `${FLYOUT_MAX_HEIGHT_RATIO * 100}vh`

  // * O cartão nasce alinhado ao ícone que o abriu e só sobe se de fato não couber até o rodapé.
  // * A conta depende da altura real dele — estimar pelo limite de 70vh empurrava para cima até
  // * cartões curtos abertos por ícones do fim do rail, que cabiam sem problema.
  const [top, setTop] = useState(anchorTop)

  useLayoutEffect(() => {
    const card = cardRef.current

    if (!card) {
      return
    }

    const margin = 8
    const maxTop = window.innerHeight - margin - card.offsetHeight

    setTop(Math.max(margin, Math.min(anchorTop, maxTop)))
  }, [anchorTop, items.length, searchTerm, searchable])

  return (
    <FlyoutCard
      ref={cardRef}
      data-menu-flyout
      role="menu"
      aria-label={title}
      style={{
        top,
        left: RAIL_WIDTH + FLYOUT_GAP,
        width: FLYOUT_WIDTH,
        maxHeight
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="flyout_header">
        {withPinIcon ? <MdPushPin className="flyout_header__pin" /> : null}
        {title}
      </div>

      {searchable ? (
        <div className="flyout_search">
          <MdSearch className="flyout_search__icon" />
          <input
            ref={searchRef}
            type="text"
            value={searchTerm}
            placeholder="Buscar no menu"
            aria-label="Buscar no menu"
            onChange={e => onSearchChange?.(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                focusRow(1)
              }
            }}
          />
          {searchTerm ? (
            <button
              type="button"
              title="Limpar busca"
              aria-label="Limpar busca"
              onClick={() => {
                onSearchChange?.('')
                searchRef.current?.focus()
              }}
            >
              <MdClose />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flyout_rows">
        {items.map((item, index) => (
          <a
            key={item.route}
            href={item.route}
            role="menuitem"
            className={`${item.route === activeRoute ? 'is-active' : ''} ${
              dropIndex === index ? 'is-drop-target' : ''
            }`}
            aria-current={item.route === activeRoute ? 'page' : undefined}
            title={
              reorderable
                ? `${item.title} — arraste para reordenar (ou Alt + setas)`
                : item.title
            }
            data-order-index={index}
            draggable={reorderable}
            onDragStart={
              reorderable
                ? e => {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', String(index))
                  }
                : undefined
            }
            onDragOver={
              reorderable
                ? e => {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    setDropIndex(index)
                  }
                : undefined
            }
            onDragLeave={reorderable ? () => setDropIndex(null) : undefined}
            onDragEnd={reorderable ? () => setDropIndex(null) : undefined}
            onDrop={
              reorderable
                ? e => {
                    e.preventDefault()
                    setDropIndex(null)

                    const from = Number(e.dataTransfer.getData('text/plain'))

                    if (!Number.isNaN(from)) {
                      onReorder?.(from, index)
                    }
                  }
                : undefined
            }
            onClick={e => {
              e.preventDefault()
              onNavigate(item.route)
            }}
          >
            <HighlightedTitle
              title={item.title}
              searchTerm={searchable ? searchTerm : undefined}
            />
          </a>
        ))}

        {searchable && !items.length ? (
          <p className="flyout_empty">
            {searchTerm ? 'Nenhum item encontrado' : 'Digite para buscar'}
          </p>
        ) : null}
      </div>
    </FlyoutCard>
  )
}
